import { toISODateString } from "./dateUtils";

/** A Luxon DateTime-like object: `sanitizeSeminarValue`'s only non-Date object case. */
interface DateTimeLike {
  toISODate?: () => string;
}

/**
 * Sanitize a single seminar form field value before it lands in the draft.
 *
 * Ported VERBATIM from the inline logic in
 * pages/seminars/SeminarEditPage.js `handleSeminarChange`:
 * - Time-of-day fields (`anfangs_zeit` / `end_zeit`) keep their full Date so the
 *   time survives on save (it was previously stripped to a date, losing the time).
 * - Any other Date becomes a YYYY-MM-DD string.
 * - A Luxon-like object (has `toISODate`) is converted via `toISODate()`.
 * - Anything else passes through unchanged.
 *
 * Pure: no React, no state.
 *
 * @param {string} name  - the field name being changed
 * @param {*} value      - the raw value from the input
 * @returns {*} the sanitized value
 */
export function sanitizeSeminarValue(name: string, value: unknown): unknown {
  let sanitizedValue: unknown = value;
  try {
    if (name === "anfangs_zeit" || name === "end_zeit") {
      sanitizedValue = value;
    } else if (value instanceof Date) {
      sanitizedValue = toISODateString(value);
    } else if (value && typeof value === "object" && (value as DateTimeLike).toISODate) {
      // Luxon-like object: `.toISODate` verified truthy above; the cast (not `any`)
      // narrows `unknown` to the minimal method shape so the call type-checks.
      sanitizedValue = (value as Required<DateTimeLike>).toISODate();
    }
  } catch (_e) {
    sanitizedValue = value;
  }
  return sanitizedValue;
}
